import { createPathMesh } from "../../assetBuilders/path";
import { BabylonState } from "../../state";

/**
 * Generates the trace mesh for all positions that have loaded.
 */
export function reloadStalePaths(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    if (
      !entity.globalPosition ||
      !entity.path ||
      entity.path.load.state !== "stale" ||
      entity.globalPosition.cache.load.state !== "loaded"
    ) {
      continue;
    }
    const path = entity.path;
    // The old trace goes whether or not a new one replaces it. A disposed mesh
    // keeps the world bounds it last had, so one left on the component would
    // have the camera keep framing a route that is no longer drawn.
    const hadTrace = path.mesh !== undefined;
    path.mesh?.dispose();
    path.material?.dispose();
    path.mesh = undefined;
    path.material = undefined;
    // Built from the *global* trajectory: the entity's own cache holds offsets
    // in its parent's frame, so tracing that would draw a path in a moving frame
    // rather than the route the entity actually took through the world.
    // Vertices stay relative to the mean so the large absolute component never
    // reaches float32 vertex data.
    const mean = entity.globalPosition.mean;
    const points = path?.spec.lineWidth
      ? entity.globalPosition.cache.allPoints.map((p) => p.subtract(mean))
      : [];
    // TODO: if the mesh creation gets especially complicated, break this up into units of work
    // to let user interactions etc happen.
    const lineWidth = path.spec.lineWidth;
    // Only make a mesh to draw if there is a line to draw. A single sample is
    // a point, not a line: Babylon builds it as a greased line with two
    // vertices and an empty index buffer, which WebGL then rejects on every
    // frame (GL_INVALID_OPERATION, "Insufficient buffer size") until the
    // console stops reporting errors for the context altogether.
    if (
      lineWidth !== undefined &&
      isFinite(lineWidth) &&
      lineWidth > 0 &&
      points.length > 1
    ) {
      path.mesh = createPathMesh(
        `${entity.id}.trace`,
        path.spec.color,
        lineWidth,
        points,
        state.context.scene,
      );
    }
    // Invalidate the third-person zoom cache: a new path mesh contributes to
    // the scene-union bounding box that `computeThirdPersonZoom` derives its
    // envelope from, and an old one dropped with nothing to replace it (a
    // trajectory down to one sample, a line width of 0) takes its share away.
    if (hadTrace || path.mesh) {
      state.sceneConfig.entityGeneration =
        (state.sceneConfig.entityGeneration ?? 0) + 1;
    }
    path.load.state = "loaded";
  }
}
